import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  ViewChild,
} from "@angular/core";

@Component({
  selector: "rs-float-box",
  templateUrl: "./index.component.html",
  styleUrls: ["./index.component.scss"],
})
export class FloatBoxComponent implements AfterViewInit, OnDestroy {
  @Input("placement") _placement: "top" | "left" | "right" | "bottom";
  @Input("position") _position: "start" | "center" | "end";
  @Input("trigger") _trigger: "hover" | "click" | "rightClick" = "hover";
  @Input("animation") _animation: "yes" | "no" = "no";
  @Input("fixed") _fixed: "yes" | "no" = "no";
  @Input("floatClass") _floatClass: string = "";
  get placement() {
    if (["top", "left", "right", "bottom"].includes(this._placement)) {
      return this._placement;
    }
    return "bottom";
  }

  get position() {
    if (["start", "center", "end"].includes(this._position)) {
      return this._position;
    }
    return "center";
  }

  get trigger() {
    if (this._trigger === "click") {
      return "click";
    }
    if (this._trigger === "rightClick") {
      return "rightClick";
    }
    return "hover";
  }

  get animation() {
    if (this._animation === "yes") {
      return "yes";
    }
    return "no";
  }

  get fixed() {
    if (this._fixed === "yes") {
      return "yes";
    }
    return "no";
  }

  @Output("openChange") openChange = new EventEmitter<boolean>();

  @ViewChild("element", { static: false })
  rootElement: ElementRef<HTMLDivElement>;
  @ViewChild("content", { static: false })
  contentElement: ElementRef<HTMLDivElement>;

  constructor(private ref: ChangeDetectorRef) {}

  ngAfterViewInit() {
    this.fixedContainerEl = this.getFixedContainer();
    if (this.fixed === "yes") {
      this.fixedContainerEl.append(this.contentElement.nativeElement);
    }
  }

  ngOnDestroy() {
    this.contentElement.nativeElement.remove();
  }

  FIXED_CONTAINER_ID = "kt-float-box-fixed-container";
  fixedContainerEl: HTMLElement;
  getFixedContainer() {
    let containerElement = document.getElementById(this.FIXED_CONTAINER_ID);
    if (!containerElement) {
      containerElement = document.createElement("div");
      containerElement.setAttribute("id", this.FIXED_CONTAINER_ID);
      document.body.append(containerElement);
    }
    return containerElement;
  }

  setFixedContentPosition() {
    if (this.fixed === "no") {
      return;
    }

    const {
      top: y,
      left: x,
      width,
      height,
    } = this.rootElement.nativeElement.getBoundingClientRect();

    let top = "0";
    let left = "0";
    let translateX = "0";
    let translateY = "0";
    switch (this.placement + "_" + this.position) {
      case "top_start":
        top = y + "px";
        left = x - 8 + "px";
        translateY = "-100%";
        break;
      case "top_center":
        top = y + "px";
        left = x + width / 2 + "px";
        translateX = "-50%";
        translateY = "-100%";
        break;
      case "top_end":
        top = y + "px";
        left = x + width + 8 + "px";
        translateX = "-100%";
        translateY = "-100%";
        break;
      case "left_start":
        top = y - 8 + "px";
        left = x + "px";
        translateX = "-100%";
        break;
      case "left_center":
        top = y + height / 2 + "px";
        left = x + "px";
        translateX = "-100%";
        translateY = "-50%";
        break;
      case "left_end":
        top = y + height + 8 + "px";
        left = x + "px";
        translateX = "-100%";
        translateY = "-100%";
        break;
      case "right_start":
        top = y - 8 + "px";
        left = x + width + "px";
        break;
      case "right_center":
        top = y + height / 2 + "px";
        left = x + width + "px";
        translateY = "-50%";
        break;
      case "right_end":
        top = y + height + 8 + "px";
        left = x + width + "px";
        translateY = "-100%";
        break;
      case "bottom_start":
        top = y + height + "px";
        left = x - 8 + "px";
        break;
      case "bottom_center":
        top = y + height + "px";
        left = x + width / 2 + "px";
        translateX = "-50%";
        break;
      case "bottom_end":
        top = y + height + "px";
        left = x + width + 8 + "px";
        translateX = "-100%";
        break;
    }

    this.contentElement.nativeElement.style.top = top;
    this.contentElement.nativeElement.style.left = left;
    this.contentElement.nativeElement.style.transform = `translate(${translateX}, ${translateY})`;
  }

  opened: "yes" | "no" = "no";
  onClickContainer(event: MouseEvent) {
		event.stopPropagation(); // 阻止点击事件冒泡到外层，避免穿透
    if (this.trigger !== "click") {
      return;
    }

    this.opened = "yes";
    this.openChange.emit(true);
    this.setFixedContentPosition();
    window.addEventListener("click", this.onClickOutside);
  }

  onClickOutside = (event: MouseEvent) => {
    let floatElement;
    if (this._floatClass) {
      floatElement = document.querySelector(`${this._floatClass}`);
    }
    if (
      !this.rootElement.nativeElement.contains(event.target as Element) &&
      !this.fixedContainerEl.contains(event.target as Element) &&
      (!floatElement || !floatElement.contains(event.target as Element))
    ) {
      this.opened = "no";
      this.openChange.emit(false);
      window.removeEventListener("click", this.onClickOutside);
      this.ref.markForCheck();
    }
  };

  onRightClickContainer(event: MouseEvent) {
    if (this.trigger !== "rightClick") {
      return;
    }

    event.preventDefault(); // 阻止默认右键菜单
    this.opened = "yes";
    this.openChange.emit(true);
    this.setFixedContentPosition();
    window.addEventListener("contextmenu", this.onContextMenuOutside);
    window.addEventListener("click", this.onContextMenuOutside);
  }

  onContextMenuOutside = (event: MouseEvent) => {
    let floatElement;
    if (this._floatClass) {
      floatElement = document.querySelector(`${this._floatClass}`);
    }
    if (
      !this.rootElement.nativeElement.contains(event.target as Element) &&
      !this.fixedContainerEl.contains(event.target as Element) &&
      (!floatElement || !floatElement.contains(event.target as Element))
    ) {
      this.opened = "no";
      this.openChange.emit(false);
      window.removeEventListener("contextmenu", this.onContextMenuOutside);
      window.removeEventListener("click", this.onContextMenuOutside);
      this.ref.markForCheck();
    }
  };

  onMouseEnter() {
    if (this.fixed === "no" || this.trigger !== "hover") {
      return;
    }

    this.opened = "yes";
    this.openChange.emit(true);
    this.setFixedContentPosition();
    window.addEventListener("mousemove", this.onMoveOutside);
  }

  onMoveOutside = (event: MouseEvent) => {
    let floatElement;
    if (this._floatClass) {
      floatElement = document.querySelector(`${this._floatClass}`);
    }
    if (
      !this.rootElement.nativeElement.contains(event.target as Element) &&
      !this.contentElement.nativeElement.contains(event.target as Element) &&
      (!floatElement || !floatElement.contains(event.target as Element))
    ) {
      this.opened = "no";
      this.openChange.emit(false);
      window.removeEventListener("click", this.onMoveOutside);
      this.ref.markForCheck();
    }
  };

  close() {
    setTimeout(() => {
      this.opened = "no";
      this.ref.markForCheck();
      this.ref.detectChanges();
      this.openChange.emit(false);
    }, 0);
  }
}
